"use client";

/**
 * glossary-tooltip.tsx
 *
 * Inline dashed-underline span that reveals a fixed-position popover with the
 * simple-English definition on hover / focus and links to the full glossary
 * entry. Uses position:fixed + getBoundingClientRect() to avoid clipping from
 * parent overflow:hidden containers.
 *
 * - Desktop: 200ms dismiss delay + invisible hover bridge so users can reach tooltip
 * - Mobile: stays open until user taps outside
 */

import { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";

export interface GlossaryTooltipProps {
  term: string;        // display text as it appears in the paragraph
  slug: string;        // glossary entry slug
  definition: string;  // simple-English definition
  children?: React.ReactNode;
}

interface TooltipCoords {
  top: number;
  left: number;
  showBelow: boolean;
}

const TOOLTIP_MAX_W = 300;
const TOOLTIP_GAP = 6;  // gap between trigger and tooltip
const VIEWPORT_PAD = 8;

export function GlossaryTooltip({
  term,
  slug,
  definition,
  children,
}: GlossaryTooltipProps) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<TooltipCoords | null>(null);
  const [mounted, setMounted] = useState(false);

  const triggerRef = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const dismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Need to know when we're in the browser for createPortal
  useEffect(() => setMounted(true), []);

  const clearDismiss = useCallback(() => {
    if (dismissTimer.current) {
      clearTimeout(dismissTimer.current);
      dismissTimer.current = null;
    }
  }, []);

  const show = useCallback(() => {
    clearDismiss();
    setOpen(true);
  }, [clearDismiss]);

  const scheduleHide = useCallback(() => {
    clearDismiss();
    dismissTimer.current = setTimeout(() => setOpen(false), 200);
  }, [clearDismiss]);

  // Calculate fixed position from trigger's bounding rect
  const updateCoords = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const tooltipHeight = tooltipRef.current?.offsetHeight ?? 120;

    // Vertical: prefer above, fall back to below if not enough room
    const showBelow = rect.top < tooltipHeight + TOOLTIP_GAP + VIEWPORT_PAD;

    const top = showBelow
      ? rect.bottom + TOOLTIP_GAP
      : rect.top - TOOLTIP_GAP;

    // Horizontal: center on trigger, clamp to viewport
    let left = rect.left + rect.width / 2 - TOOLTIP_MAX_W / 2;
    left = Math.max(VIEWPORT_PAD, left);
    left = Math.min(window.innerWidth - TOOLTIP_MAX_W - VIEWPORT_PAD, left);

    setCoords({ top, left, showBelow });
  }, []);

  // Recalculate position when tooltip opens and on scroll/resize
  useEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }

    updateCoords();

    // Recalc on scroll/resize since position:fixed doesn't track scroll
    window.addEventListener("scroll", updateCoords, true);
    window.addEventListener("resize", updateCoords);
    return () => {
      window.removeEventListener("scroll", updateCoords, true);
      window.removeEventListener("resize", updateCoords);
    };
  }, [open, updateCoords]);

  // Re-measure after tooltip renders (actual height may differ from estimate)
  useEffect(() => {
    if (open && tooltipRef.current) updateCoords();
  }, [open, updateCoords]);

  // Mobile: close on tap outside
  useEffect(() => {
    if (!open) return;

    function handleTouchOutside(e: TouchEvent) {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        tooltipRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    }

    document.addEventListener("touchstart", handleTouchOutside);
    return () => document.removeEventListener("touchstart", handleTouchOutside);
  }, [open]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (dismissTimer.current) clearTimeout(dismissTimer.current);
    };
  }, []);

  // Build the hover bridge: an invisible element connecting trigger to tooltip
  // so mouse can travel across the gap without triggering dismiss
  const bridgeStyle = (): React.CSSProperties | undefined => {
    if (!coords || !triggerRef.current) return undefined;
    const rect = triggerRef.current.getBoundingClientRect();

    if (coords.showBelow) {
      return {
        position: "fixed",
        left: rect.left,
        top: rect.bottom,
        width: rect.width,
        height: TOOLTIP_GAP + 2,
        zIndex: 9998,
      };
    }
    return {
      position: "fixed",
      left: rect.left,
      top: rect.top - TOOLTIP_GAP - 2,
      width: rect.width,
      height: TOOLTIP_GAP + 2,
      zIndex: 9998,
    };
  };

  const tooltipContent = open && coords && mounted
    ? createPortal(
        <>
          {/* Invisible bridge between trigger and tooltip */}
          <div
            style={bridgeStyle()}
            onMouseEnter={show}
            onMouseLeave={scheduleHide}
            aria-hidden
          />
          {/* Tooltip */}
          <div
            ref={tooltipRef}
            role="tooltip"
            onMouseEnter={show}
            onMouseLeave={scheduleHide}
            style={{
              position: "fixed",
              zIndex: 9999,
              top: coords.showBelow ? coords.top : undefined,
              bottom: coords.showBelow
                ? undefined
                : window.innerHeight - coords.top,
              left: coords.left,
              maxWidth: TOOLTIP_MAX_W,
              width: "max-content",
            }}
            className="rounded-lg bg-navy border border-surface-light/30 shadow-xl p-3 text-left pointer-events-auto"
          >
            <span className="block text-blue text-[10px] uppercase tracking-wide font-semibold mb-1">
              {term}
            </span>
            <span className="block text-muted-foreground text-[11px] leading-relaxed mb-2 break-words max-h-[200px] overflow-y-auto">
              {definition}
            </span>
            <Link
              href={`/glossary#${slug}`}
              className="text-blue text-[11px] hover:underline no-underline"
            >
              Full definition →
            </Link>
          </div>
        </>,
        document.body
      )
    : null;

  return (
    <span
      ref={triggerRef}
      className="inline"
      onMouseEnter={show}
      onMouseLeave={scheduleHide}
      onFocus={show}
      onBlur={scheduleHide}
      onClick={(e) => {
        e.stopPropagation();
        setOpen((prev) => !prev);
      }}
    >
      <span
        tabIndex={0}
        className="border-b border-dashed border-blue/60 text-foreground cursor-help"
      >
        {children ?? term}
      </span>

      {tooltipContent}
    </span>
  );
}
